import React from 'react';
import PropTypes from "prop-types";
import {iconArrowRight, iconX} from "../common/Icon";
import {Button, Components, delayedInput, InputInformation, InputList, MaskType, PopupWindow, Size, TextInput, ValidationEngine, ValidationStyle, ValidationType} from "@utahdts/dts-react-common";
import clone from "clone";
import {ResponsiveBar} from "@nivo/bar";
import {BasicTooltip, useTooltip} from "@nivo/tooltip";
import {format} from "d3-format";
import {COLOR_PALETTE, formatAmount, MAX_EDITOR_CHARACTERS} from "../misc/Shared";
import Editor from "./Editor";
import reduxStore from "../app/ReduxStore";
import webservice from "../common/Webservice";
import {isEmpty} from "lodash";
import {line} from "d3-shape";

const propTypes = {
	graph: PropTypes.object.isRequired,
	onClose: PropTypes.func.isRequired
};

const defaultProps = {};

class GraphEditor extends React.Component {
	constructor(props) {
		super(props);
		this.inputInformationChanged = this.inputInformationChanged.bind(this);
		this.chartInputChanged = this.chartInputChanged.bind(this);
		this.columnInputChanged = this.columnInputChanged.bind(this);
		this.createGraph = this.createGraph.bind(this);
		this.saveGraph = this.saveGraph.bind(this);
		this.validateGraph = this.validateGraph.bind(this);
		this.targetLayer = this.targetLayer.bind(this);

		this.state = {
			graph: this.props.graph,
			barChart: [],
			barChartColumns: [],
			barChartKeys: [],
			barChartKeysDisplay: [],
			hasTarget: false,
			highestValue: 'auto',
			showError: false
		};

		this.DelayedTextInput = delayedInput(TextInput);

		this.inputs = [
			new InputInformation({
				field: 'title',
				label: 'Title:',
				type: Components.TEXT_INPUT,
				size: Size.FULL_WIDTH,
				onChange: this.inputInformationChanged,
				className: "stack-form full-width",
				maxLengthCounter: 100,
			}),
			new InputInformation({
				field: 'axisTitleX',
				label: 'X axis:',
				type: Components.TEXT_INPUT,
				size: Size.FULL_WIDTH,
				onChange: this.inputInformationChanged,
				className: "stack-form fifty",
			}),
			new InputInformation({
				field: 'unitTypeX',
				label: 'X units:',
				type: Components.TEXT_INPUT,
				size: Size.FULL_WIDTH,
				onChange: this.inputInformationChanged,
				className: "stack-form fifty",
			}),
			new InputInformation({
				field: 'axisTitleY',
				label: 'Y axis:',
				type: Components.TEXT_INPUT,
				size: Size.FULL_WIDTH,
				onChange: this.inputInformationChanged,
				className: "stack-form fifty",
			}),
			new InputInformation({
				field: 'unitTypeY',
				label: 'Y units:',
				type: Components.TEXT_INPUT,
				size: Size.FULL_WIDTH,
				onChange: this.inputInformationChanged,
				className: "stack-form fifty",
			}),
			new InputInformation({
				field: 'displayOrder',
				label: 'Display order:',
				type: Components.TEXT_INPUT,
				size: Size.FULL_WIDTH,
				onChange: this.inputInformationChanged,
				className: "stack-form fifty",
			}),
			new InputInformation({
				field: 'amountPrefix',
				label: 'Amount prefix:',
				type: Components.TEXT_INPUT,
				size: Size.FULL_WIDTH,
				onChange: this.inputInformationChanged,
				className: "stack-form fifty",
			}),
			new InputInformation({
				field: 'amountSuffix',
				label: 'Amount suffix:',
				type: Components.TEXT_INPUT,
				size: Size.FULL_WIDTH,
				onChange: this.inputInformationChanged,
				className: "stack-form fifty",
			})
		];

		this.validationEngine = new ValidationEngine({
			'title': [ValidationType.REQUIRED],
			'displayOrder': [ValidationType.REQUIRED, ValidationType.IS_INT],
		});
		this.validationEngine.setValidationStyle(ValidationStyle.BLURRED);
	}

	componentDidMount() {
		this.convertDataToChart();
	}

	inputInformationChanged(field, value) {
		this.setState({graph: Object.assign(clone(this.state.graph), {[field]: value})});
	}

	convertDataToChart() {
		let data = this.props.graph;
		let barChart = data.columns.sort((a,b) => a.displayOrder - b.displayOrder).map(column => {
			let newBarChart = { indexBy: column.title };
			let columnData = data.data.filter(item => item.columnTitle === column.title);
			columnData.forEach(data => {
				newBarChart[data.rowTitle] = data.amount;
			});
			return newBarChart;
		});
		let barChartColumns = data.columns.map(column => column.title);
		let barChartKeys = data.rows.map(row => row.title);
		let barChartKeysDisplay = clone(barChartKeys);
		let highestValue = Math.max(...data.data.map(i => i.amount));

		let hasTarget = false;
		const index = barChartKeys.indexOf("Target");
		if (index > -1) {
			hasTarget = true;
			barChartKeysDisplay.splice(index, 1);
		}

		this.setState({
			barChart: barChart,
			barChartColumns: barChartColumns,
			barChartKeys: barChartKeys,
			barChartKeysDisplay: barChartKeysDisplay,
			hasTarget: hasTarget,
			highestValue: isNaN(highestValue) ? 'auto' : highestValue * 1.1,
		});
	}

	convertChartData() {
		const store = reduxStore.getState();
		return {
			graphId: this.state.graph.graphId,
			cobiId: store.app.currentCobiId,
			sessionFiscalYear: store.app.currentYear,
			displayOrder: this.state.graph.displayOrder,
			title: this.state.graph.title,
			axisTitleX: this.state.graph.axisTitleX,
			unitTypeX: this.state.graph.unitTypeX,
			axisTitleY: this.state.graph.axisTitleY,
			unitTypeY: this.state.graph.unitTypeY,
			description: this.state.graph.description,
			isActive: this.state.graph.isActive,
			amountPrefix: this.state.graph.amountPrefix,
			amountSuffix: this.state.graph.amountSuffix,
			columns: this.state.barChartColumns.map((column, index) => ({title: column, displayOrder: index + 1})),
			rows: this.state.barChartKeys.map((row, index) => ({title: row, displayOrder: index + 1})),
			data: this.state.barChart.reduce((acc, item) => {
				let column = item.indexBy;
				let rows = Object.keys(item);
				rows.shift();
				return acc.concat(rows.map(key => ({
					columnTitle: column,
					rowTitle: key,
					amount: item[key],
				})));
			}, []),
		};
	}

	renderChartTable() {
		let table, header = [], body = [], footer = [];

		header.push(<th key={'headerFirst'} className="no-padding"/>);
		footer.push(<td key={'footerFirst'} onClick={() => this.addRow()} className="text-center button button--add">Add row {iconArrowRight("ninety")}</td>);

		this.state.barChartKeys.forEach((key, keyIndex) => {
			body.push(<tr key={'row' + keyIndex}>
				<td>
					<this.DelayedTextInput field={'rowTitle' + keyIndex}
							   label="None"
							   showLabel={false}
							   value={key}
							   onChange={(field, value) => {
								   this.rowInputChanged(value, key, keyIndex);
							   }}/>
				</td>
				{this.state.barChartColumns.map((column, columnIndex) => {
					return (<td key={key + columnIndex}>
						<this.DelayedTextInput field={key + columnIndex}
								   label="None"
								   showLabel={false}
								   value={this.state.barChart[columnIndex][key]}
								   onChange={(field, value) => {
									   this.chartInputChanged(key, columnIndex, value);
								   }}
								   maskType={MaskType.DECIMAL}/>
					</td>);
				})}
				<td onClick={() => this.removeRow(keyIndex)} className="text-center button button--delete">Delete row {iconX()}</td>
			</tr>);
		});

		this.state.barChartColumns.forEach((c, index) => {
			header.push(<th key={'header' + index}><TextInput field={"column" + c}
															  label="None"
															  showLabel={false}
															  value={c}
															  onChange={(field, value) => this.columnInputChanged(value, c, index)}/></th>);
			footer.push(<td key={'footer' + index} onClick={() => this.removeColumn(c, index)} className="text-center button button--delete">Delete column {iconX()}</td>);
		});

		header.push(<th key={'headerLast'} onClick={() => this.addColumn()} className="text-center button button--add">Add column {iconArrowRight()}</th>);
		footer.push(<td key={'footerLast'}/>);

		table = (
			<div className="chart-table--scroller">
				<table className="chart-table">
					<thead>
						<tr>{header}</tr>
					</thead>
					<tbody>{body}</tbody>
					<tfoot>
						<tr>{footer}</tr>
					</tfoot>
				</table>
			</div>
		);

		return table;
	}

	chartInputChanged(key, columnIndex, value) {
		let items = clone(this.state.barChart);
		let item = items[columnIndex];
		item[key] = parseFloat(value);
		let values = [];
		for(let i = 0; i < items.length; i++) {
			Object.keys(items[i]).forEach(k => {
				if(k !== 'indexBy') {
					values.push(items[i][k]);
				}
			});
		}
		let highestValue = Math.max(...values);
		this.setState({barChart: items, highestValue: isNaN(highestValue) ? 'auto' : highestValue * 1.1,});
	}

	columnInputChanged(value, oldValue, index) {
		let error = false;
		let barChartColumns = clone(this.state.barChartColumns);
		if(barChartColumns.includes(value)){
			value = oldValue;
			error = true;
		}
		barChartColumns[index] = value;
		let items = clone(this.state.barChart);
		items.find(i => i.indexBy === oldValue).indexBy = value;
		this.setState({barChartColumns: barChartColumns, barChart: items, showError: error, errorMessage: 'Two columns can not have the same name!'});
	}

	rowInputChanged(value, oldValue, index) {
		let error = false;
		let barChartKeys = clone(this.state.barChartKeys);
		if(barChartKeys.includes(value)){
			value = oldValue;
			error = true;
		}
		barChartKeys[index] = value;
		let barChartKeysDisplay = clone(barChartKeys);
		let hasTarget = false;
		const i = barChartKeys.indexOf("Target");
		if (i > -1) {
			hasTarget = true;
			barChartKeysDisplay.splice(i, 1);
		}
		let items = clone(this.state.barChart);
		items.forEach(item => delete Object.assign(item, {[value]: item[oldValue] })[oldValue]);
		this.setState({barChartKeys: barChartKeys, barChart: items, barChartKeysDisplay: barChartKeysDisplay, hasTarget: hasTarget, showError: error, errorMessage: 'Two rows can not have the same name!'});
	}

	addRow() {
		let barChartKeys = clone(this.state.barChartKeys);
		let items = clone(this.state.barChart);

		let newRowTitle = 'Row ' + (barChartKeys.length + 1);
		// Make sure no row have the same id
		if(barChartKeys.includes(newRowTitle)) {
			let i = barChartKeys.length + 2;
			while (barChartKeys.includes(newRowTitle)) {
				newRowTitle = 'Row ' + i;
				i++;
			}
		}

		items.forEach(item => item[newRowTitle] = 0);
		barChartKeys.push(newRowTitle);

		let barChartKeysDisplay = clone(barChartKeys);
		let hasTarget = false;
		const index = barChartKeys.indexOf("Target");
		if (index > -1) {
			hasTarget = true;
			barChartKeysDisplay.splice(index, 1);
		}

		this.setState({barChart: items, barChartKeys: barChartKeys, barChartKeysDisplay: barChartKeysDisplay, hasTarget: hasTarget});
	}

	removeRow(index) {
		let barChartKeys = clone(this.state.barChartKeys);
		let key = barChartKeys[index];
		barChartKeys.splice(index,1);

		let items = clone(this.state.barChart);
		items.forEach(item => delete item[key]);

		let barChartKeysDisplay = clone(barChartKeys);
		let hasTarget = false;
		const i = barChartKeys.indexOf("Target");
		if (i > -1) {
			hasTarget = true;
			barChartKeysDisplay.splice(i, 1);
		}

		let values = [];
		for(let i = 0; i < items.length; i++) {
			Object.keys(items[i]).forEach(k => {
				if(k !== 'indexBy') {
					values.push(items[i][k]);
				}
			});
		}
		let highestValue = Math.max(...values);
		this.setState({barChart: items, barChartKeys: barChartKeys, barChartKeysDisplay: barChartKeysDisplay, hasTarget: hasTarget, highestValue: isNaN(highestValue) ? 'auto' : highestValue * 1.1});
	}

	addColumn() {
		let barChart = clone(this.state.barChart);
		let barChartColumns = clone(this.state.barChartColumns);
		let barChartKeys = clone(this.state.barChartKeys);
		let newColumnTitle = 'Column ' + (barChartColumns.length + 1);
		// Make sure no column have the same title
		if(barChartColumns.includes(newColumnTitle)) {
			let i = barChartColumns.length + 2;
			while (barChartColumns.includes(newColumnTitle)) {
				newColumnTitle = 'Column ' + i;
				i++;
			}
		}
		let newColumn = { indexBy: newColumnTitle};

		barChartColumns.push(newColumnTitle);
		barChartKeys.forEach(key => newColumn[key] = 0);
		barChart.push(newColumn);

		this.setState({barChart: barChart, barChartColumns: barChartColumns});
	}

	removeColumn(column, index) {
		let barChart = clone(this.state.barChart);
		let barChartColumns = clone(this.state.barChartColumns);
		barChartColumns.splice(index,1);
		let barChartIndex = barChart.findIndex(item => item.indexBy === column);
		barChart.splice(barChartIndex,1);
		let values = [];
		for(let i = 0; i < barChart.length; i++) {
			Object.keys(barChart[i]).forEach(k => {
				if(k !== 'indexBy') {
					values.push(barChart[i][k]);
				}
			});
		}
		let highestValue = Math.max(...values);
		this.setState({barChart: barChart, barChartColumns: barChartColumns, highestValue: isNaN(highestValue) ? 'auto' : highestValue * 1.1});
	}

	validateGraph() {
		this.validationEngine.setValidationStyle(ValidationStyle.ALWAYS);
		let isValid = this.validationEngine.isValid(this.state.graph);
		let showError = (isEmpty(this.state.barChartColumns) && !isEmpty(this.state.barChartKeys)) || (isEmpty(this.state.barChartKeys) && !isEmpty(this.state.barChartColumns));
		let errorMessage = showError ? 'At least one row and one column are required to create a graph.' : '';
		if (isValid) {
			isValid = this.state.graph.title && this.state.graph.title.length <= 100;
			if (!isValid) {
				alert('Title is too long. Please reduce its character count and try again.')
			}
		}
		this.setState({showError: showError, errorMessage: errorMessage}, () => isValid && !showError ? this.state.graph.graphId ? this.saveGraph() : this.createGraph() : undefined);
	}

	saveGraph() {
		webservice.admin.performance.update(this.convertChartData()).then(this.props.onClose).catch(reason => console.log(reason));
	}

	createGraph() {
		webservice.admin.performance.create(this.convertChartData()).then(this.props.onClose).catch(reason => console.log(reason));
	}

	targetLayer({ bars, xScale, yScale }){
		if(this.state.hasTarget){
			const filterBars = bars.map(bar => ({value: bar.data.data.Target, column: bar.data.data.indexBy, width: bar.width, key: bar.key}));
			const distinct = [];
			const map = new Map();
			for (const item of filterBars) {
				if(!map.has(item.column)){
					map.set(item.column, true);
					distinct.push({
						value: item.value,
						column: item.column,
						width: item.width,
						key: item.key
					});
				}
			}

			const lineGenerator = line()
				.x(bar => xScale(bar.column) + bar.width / 2)
				.y(bar => yScale(bar.value));

			const { showTooltipFromEvent, hideTooltip } = useTooltip();

			return (
				<>
					<path
						d={lineGenerator(distinct)}
						fill="none"
						stroke={"rgba(245, 142, 39, .85)"}
						strokeWidth="3"
						style={{ pointerEvents: "none" }}
					/>
					{distinct.map(bar => (
						<circle
							key={bar.key}
							cx={xScale(bar.column) + bar.width / 2}
							cy={yScale(bar.value)}
							r={6}
							fill="white"
							stroke={"rgba(245, 142, 39, 1)"}
							strokeWidth="2"
							onMouseEnter={e => showTooltipFromEvent(<BasicTooltip id="Target" value={formatAmount(bar.value, 2, this.state.graph.amountPrefix, this.state.graph.amountSuffix)} />, e)}
							onMouseLeave={() => hideTooltip()}
							onMouseMove={e => showTooltipFromEvent(<BasicTooltip id="Target" value={formatAmount(bar.value, 2, this.state.graph.amountPrefix, this.state.graph.amountSuffix)} />, e)}
						/>
					))}
				</>
			);
		}

		return <></>;
	}

	render() {
		return (
			<PopupWindow
				footerChildren={<React.Fragment>
					<Button label="Cancel" onClick={this.props.onClose} className="grey"/>
					<Button label="Save graph"
							className="blue"
							disabled={this.state.graph.description.length > MAX_EDITOR_CHARACTERS}
							onClick={this.validateGraph}/>
				</React.Fragment>}
				className="popup-window--xlarge">
				<div>
					<h3>Graph info</h3>
					<InputList inputs={this.inputs}
							   data={this.state.graph}
							   className="flex flex-wrap mb-spacing stack-form--container"
							   validationEngine={this.validationEngine}/>
					<div className="mt-spacing-small mb-spacing flex align-center">
						<label htmlFor="isActive">Active</label>
						<label className="switch ml-spacing-small">
							<input
								type="checkbox"
								name="isActive"
								checked={this.state.graph.isActive}
								onChange={e => this.inputInformationChanged("isActive", e.target.checked)}
							/>
							<span className="slider round"/>
						</label>
					</div>
					<label>Description:</label>
					<Editor html={this.state.graph.description}
							onChange={model => this.setState(prevState => ({
								graph: {
									...prevState.graph,
									description: model,
								}
							}))}
							counter={true}/>
					<h3 className="italic-text mt-spacing-large">Add data</h3>
					<p><i>Note:</i> to add a line for the target, create a row and name it "Target" and enter the data accordingly.</p>
					{this.state.showError && <p className="note note--error mtb-spacing">{this.state.errorMessage}</p>}
					<div className="chart-table--wrapper mt-spacing-large">
						{this.renderChartTable()}
					</div>
					<div className="bar-graph">
						<ResponsiveBar
							data={this.state.barChart}
							keys={this.state.barChartKeysDisplay}
							indexBy="indexBy"
							margin={{
								"top": 50,
								"right": 20,
								"bottom": 100,
								"left": 70
							}}
							padding={0.3}
							borderColor="inherit:darker(1.6)"
							axisBottom={{
								"orient": "bottom",
								"tickSize": 5,
								"tickPadding": 5,
								"legend": this.state.graph.axisTitleX + ` ${this.state.graph.unitTypeX ? `(${this.state.graph.unitTypeX})` : ''}`,
								"legendPosition": "middle",
								"legendOffset": 32
							}}
							axisLeft={{
								"orient": "left",
								"tickSize": 5,
								"tickPadding": 5,
								"tickRotation": 0,
								"legend": this.state.graph.axisTitleY + ` ${this.state.graph.unitTypeY ? `(${this.state.graph.unitTypeY})` : ''}`,
								"legendPosition": "middle",
								"legendOffset": -60,
								"format": v =>  v > 100 ? format(".2s")(v).replace(/G/,"B") : v
							}}
							labelSkipWidth={12}
							labelSkipHeight={12}
							labelTextColor="inherit:darker(1.6)"
							animate={true}
							motionStiffness={90}
							motionDamping={15}
							enableLabel={false}
							colors={COLOR_PALETTE}
							maxValue={this.state.highestValue}
							tooltip={(data) => {
								return <BasicTooltip id={data.id} value={formatAmount(data.value, 2, this.state.graph.amountPrefix, this.state.graph.amountSuffix)}/>;
							}}

							layers={['grid', 'axes', 'bars', this.targetLayer, 'markers', 'legends', 'annotations']}
						/>
					</div>
				</div>
			</PopupWindow>
		);
	}
}

GraphEditor.propTypes = propTypes;
GraphEditor.defaultProps = defaultProps;

export default GraphEditor;
